<template>
  <div class="supplement-types text-dark" :class="{'supplement-types--full': isInOpusModal}">
    <div class="supplement-types__header">
      <div class="supplement-types__header__left">
        <h2>Faste tillæg</h2>
        <ChangeSchoolYear :white-theme="true"></ChangeSchoolYear>
      </div>
      <div class="supplement-types__header__left">
        <button v-if="!isInOpusModal" class="do-bttn do-bttn--submit" @click="setIsCreateModal(true)">Opret tillæg</button>
      </div>
    </div>
    <div class="supplement-types__header">
      <div class="supplement-types__header__left">
        <Tabs v-if="!isInOpusModal"
              :tabs="supplementTabs"
              :selected="selectedTab"
              @select="selectedTab = $event">
        </Tabs>
        <Tabs v-else
              :tabs="stateTabs"
              :selected="stateFilter"
              @select="stateFilter = $event">
        </Tabs>
        <button class="do-bttn" @click="asyncLoadXLSXAndDownloadSimple" :class="{'do-bttn--loading': isDownloading}">
          Download til excel
        </button>
      </div>
      <div class="supplement-types__header__left">
        <button class="do-bttn" v-if="response.length > 0" @click="isShowResponseModal = true">Vis status</button>
        <button class="do-bttn"
                v-if="selectedTab === '(Lønbrøk)'"
                :class="{'do-bttn--submit--loading': isGeneratingSupplementType === 'lønbrøk'}"
                @click="generateSalarySupplements('lønbrøk')">
          Hent lønbrøk
        </button>
        <button class="do-bttn"
                :class="{'do-bttn--submit--loading': isGeneratingSupplementType === 'undervisningstillæg'}"
                @click="generateSalarySupplements('undervisningstillæg')"
                v-if="selectedTab === 'undervisningstillæg'">
          Hent UV-tillæg
        </button>
        <button class="do-bttn"
                @click="isShowUvLimitsModal = true"
                v-if="selectedTab === 'undervisningstillæg'">
          Se og rediger UV-grænser
        </button>
        <button class="do-bttn"
                :class="{'do-bttn--submit--loading': isGeneratingSupplementType === 'specialskoletillæg'}"
                @click="generateSalarySupplements('specialskoletillæg')"
                v-if="selectedTab === 'specialskoletillæg'">
          Hent spec-skole UV-tillæg
        </button>
        <button class="do-bttn"
                @click="isShowUvLimitsModal = true"
                v-if="selectedTab === 'specialskoletillæg'">
          Se og rediger UV-grænser
        </button>
        <button class="do-bttn"
                :class="{'do-bttn--submit--loading': isGeneratingSupplementType === 'opgaver'}"
                @click="generateSalarySupplements('opgaver')"
                v-if="selectedTab === 'opgaver'">
          Hent opgaver med lønkoder
        </button>
        <button class="do-bttn do-bttn--submit"
                @click="setIsShowTasksModal(true)"
                v-if="selectedTab === 'opgaver'">
          Tilføj lønkode til opgaver
        </button>
      </div>
    </div>
    <div>
      <div v-if="isLoading">
        <Loader></Loader>
      </div>
      <table class="do-table" :class="{'do-table--fixed': isInOpusModal}" v-else>
        <thead>
        <tr>
          <th>Navn</th>
          <th :class="{'td-identitet': isInOpusModal}">{{ isInOpusModal ? 'Ansættelse' : 'Identitet' }}</th>
          <th>Brutto årsnorm</th>
          <th class="width-period">Beregningsperiode</th>
          <th>Lønkode</th>
          <th>Omkostningssted</th>
          <th>Enheder i periode</th>
          <th>Antal udbetalinger</th>
          <th>Enh. pr måned</th>
          <th v-if="isInOpusModal && isSalaryIntegrationActive">Status</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="supplementsFiltered.length === 0">
          <td :colspan="(isInOpusModal && isSalaryIntegrationActive) ? 10 : 9">
            <i>Ingen oprettet i skoleåret</i>
          </td>
        </tr>
        <tr class="supplement-list-item" v-for="supplement in supplementsFiltered" :key="supplement.id"
            :class="{
              'supplement-list-item--locked': isInOpusModal && stateFilter === 'done'
            }"
            @click="setEditSupplement(supplement)">
          <td>
            {{ supplement.vUserName }}
          </td>
          <td v-if="isInOpusModal" class="td-identitet" @click.stop>
            <template v-if="stateFilter === 'done'">
              <p class="employment-selected">{{ selectedEmploymentLabel(supplement) }}</p>
            </template>
            <template v-else>
              <select
                class="do-select"
                :value="supplement.employment_id || ''"
                :disabled="savingEmploymentIds.includes(supplement.id)"
                @change="onSelectEmployment(supplement, $event)">
                <option value="">Vælg ansættelse</option>
                <option v-for="emp in selectableEmploymentsForUser(supplement.user_id)"
                        :key="emp.id"
                        :value="emp.id">
                  {{ employmentLabel(emp) }}
                </option>
              </select>
              <span v-if="selectableEmploymentsForUser(supplement.user_id).length === 0" class="employment-select__empty">
                Ingen ansættelse med stillingskategori
              </span>
            </template>
          </td>
          <td v-else></td>
          <td>{{ supplement.year_norm_hours }}</td>
          <td class="width-period">{{ formatPeriod(supplement.valid_period) }}</td>
          <td>{{ salaryCodeDisplay(supplement) }}</td>
          <td>{{ costCenterDisplay(supplement) }}</td>
          <td>{{ formatDecimal(supplement.units) }}</td>
          <td>{{ supplement.installments }}</td>
          <td>{{ formatDecimal(supplement.units_per_installment) }}</td>
          <td v-if="isInOpusModal && isSalaryIntegrationActive">
            <span class="supplement-state" :class="'supplement-state--' + (supplement.state || 'draft')">
              {{ stateLabel(supplement.state) }}
            </span>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <CreateSupplement
      v-if="isCreateModal"
      :isSubmitting="isSubmitting"
      :schoolId="schoolId"
      :schoolYearId="schoolYearId"
      :schoolYear="schoolYear"
      :editSupplement="editSupplement"
      :isInOpusModal="isInOpusModal"
      @save="createSupplement"
      @update="updateSupplement"
      @close="setIsCreateModal(false)"
      @delete="deleteSupplement"
    ></CreateSupplement>
    <TaskOverviewSettings
      v-if="isShowTasksModal"
      :isSupplements="true"
      @close="setIsShowTasksModal(false)"
    ></TaskOverviewSettings>
    <ShowResponse
      v-if="isShowResponseModal"
      :users="response"
      @close="isShowResponseModal = false">
    </ShowResponse>
    <UvLimitsModal
      v-if="isShowUvLimitsModal"
      :schoolId="schoolId"
      :schoolYearId="schoolYearId"
      @supplement-created="readSupplements"
      @close="isShowUvLimitsModal = false"
    ></UvLimitsModal>
  </div>
</template>

<script>
import docendo from '../../docendo'
import moment from 'moment'
import Loader from '../common/Loader.vue'
import CreateSupplement from './CreateSupplement.vue'
import ChangeSchoolYear from '../School/ChangeSchoolYear.vue'
import Tabs from '../common/Tabs.vue'
import TaskOverviewSettings from '../TaskOverviewSettings/TaskOverviewSettings.vue'
import ShowResponse from './ShowResponse.vue'
import UvLimitsModal from './UvLimitsModal.vue'
import eventbus from '../../eventbus'

let XLSX = null
export default {
  components: {ShowResponse, TaskOverviewSettings, Tabs, ChangeSchoolYear, CreateSupplement, Loader, UvLimitsModal},
  props: {
    schoolId: {
      default: () => null
    },
    schoolYear: {
      default: () => null
    },
    schoolYearId: {
      default: () => null
    },
    isInOpusModal: {
      type: Boolean,
      default: false
    },
    // Hvilken automatisk beregning der er valgt (§5 stk. 2, §5 stk. 9/10, pædagog eneansvar).
    // Bruges til senere at filtrere lønkoder, så de passer til den valgte beregning.
    beregningType: {
      type: String,
      default: null
    },
    // Optælles af modalens footer-knap for at udløse afsendelse til OPUS.
    sendToOpusTrigger: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      isLoading: true,
      supplements: [],
      isCreateModal: false,
      isSubmitting: false,
      theEditSupplementType: null,
      selectedTab: '',
      isShowTasksModal: false,
      isDownloading: false,
      editSupplement: null,
      response: [],
      isShowResponseModal: false,
      isShowUvLimitsModal: false,
      isGeneratingSupplementType: null,
      isApproving: false,
      savingEmploymentIds: [],
      stateFilter: 'draft'
    }
  },
  computed: {
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    salaryCodesById () {
      const map = {}
      this.salaryCodes.forEach(salaryCode => {
        map[salaryCode.id] = salaryCode
      })
      return map
    },
    // Opgaver-fanen åbnes via to forskellige beregninger (DSA/tale-høre og pædagog med
    // eneansvar). Vi splitter visningen ved at afgrænse til hver beregnings egne lønkoder.
    // SD identificeres både på lønart og 6-cifret tillægsnummer; KMD kun på lønart.
    beregningFilters () {
      const isSd = this.$store.getters.salarySystem === 'SD'
      return {
        paedagog_eneansvar: {
          loenarter: isSd ? ['831', '832'] : ['5061', '5062'],
          tillaegsnumre: []
        },
        dsa_tale_hoere: {
          loenarter: isSd ? ['878', '879', '835'] : ['6109', '6119', '6111'],
          tillaegsnumre: isSd ? ['111137', '116229'] : []
        }
      }
    },
    // Lønkoder der regnes som undervisningstillæg (§5 stk. 2). KMD kun på lønart;
    // SD både på lønart og 6-cifret tillægsnummer.
    uvSalaryCodes () {
      const isSd = this.$store.getters.salarySystem === 'SD'
      return {
        loenarter: isSd ? ['833', '848', '853', '854'] : ['5652', '5653'],
        tillaegsnumre: isSd ? ['111122', '111128', '111836', '111841'] : []
      }
    },
    // Spec-skole uv-tillæg kan kun bruges hvis den tilhørende lønkode er oprettet.
    // KMD: lønart 8411. SD: tillægsnummer 160099/160100 eller lønart 836.
    hasSpecialSalaryCode () {
      if (this.$store.getters.salarySystem === 'SD') {
        return this.salaryCodes.some(salaryCode =>
          String(salaryCode.tillaegsnummer) === '160099' ||
          String(salaryCode.tillaegsnummer) === '160100' ||
          String(salaryCode.loenart) === '836')
      }
      return this.salaryCodes.some(salaryCode => String(salaryCode.loenart) === '8411' || String(salaryCode.code) === '8411')
    },
    supplementTabs () {
      const isSd = this.$store.getters.salarySystem === 'SD'
      const tabs = [
        {id: '', title: 'Alle'},
        {id: '(Lønbrøk)', title: 'Lønbrøk'},
        {id: 'undervisningstillæg', title: 'UV-tillæg'},
        {id: 'specialskoletillæg', title: 'Spec-skole uv-tillæg'},
        {id: 'opgaver', title: 'Opgaver'}
      ]
        // Lønbrøk kan kun sendes til SD (backenden opretter en lønændring, ikke et tillæg).
        .filter(tab => tab.id !== '(Lønbrøk)' || isSd)
        // Spec-skole uv-tillæg er midlertidigt skjult for SD.
        .filter(tab => tab.id !== 'specialskoletillæg' || (this.hasSpecialSalaryCode && !isSd))
      if (this.isInOpusModal) {
        return tabs.filter(tab => tab.id !== '' && tab.id !== '(Lønbrøk)')
      }
      return tabs
    },
    // Den valgte automatiske beregning afgør hvilken fane der vises i modalen.
    // §5 stk. 2 = undervisningstillæg, mens §5 stk. 9/10 og pædagog med eneansvar er opgaver.
    tabForBeregningType () {
      const map = {
        loenbroek: '(Lønbrøk)',
        uv_over_graense: 'undervisningstillæg',
        spec_skole_uv: 'specialskoletillæg',
        dsa_tale_hoere: 'opgaver',
        paedagog_eneansvar: 'opgaver'
      }
      return map[this.beregningType] || 'undervisningstillæg'
    },
    isOpusIntegrationActive () {
      const features = this.$store.getters.schoolFeatures || []
      return features.indexOf('kmd_opus_integration') > -1
    },
    isSalaryIntegrationActive () {
      const features = this.$store.getters.schoolFeatures || []
      return features.indexOf('kmd_opus_integration') > -1 || features.indexOf('silkeborg_data_integration') > -1
    },
    // Lønsystemet bestemmer hvor de godkendte tillæg sendes hen (OPUS for KMD, SD for Silkeborg Data).
    salaryTargetLabel () {
      return this.$store.getters.salarySystem === 'SD' ? 'SD' : 'OPUS'
    },
    uvSupplementsToApprove () {
      return this.supplementsSorted.filter(supplement => this.isUvSupplement(supplement))
    },
    specialSkoleSupplementsToApprove () {
      return this.supplementsSorted.filter(supplement => this.isSpecialSkoleSupplement(supplement))
    },
    // Tillæg der kan sendes til OPUS afhænger af den valgte fane (UV-tillæg eller Opgaver).
    // Kun kladder kan sendes – godkendte/sendte må ikke sendes til lønsystemet igen.
    supplementsToApprove () {
      let list
      if (this.selectedTab === '(Lønbrøk)') {
        // Lønbrøk kan kun sendes til SD (backenden opretter en lønændring, ikke et tillæg).
        list = this.$store.getters.salarySystem === 'SD'
          ? this.supplementsSorted.filter(supplement => String(supplement.code) === '(Lønbrøk)')
          : []
      } else if (this.selectedTab === 'undervisningstillæg') {
        list = this.uvSupplementsToApprove
      } else if (this.selectedTab === 'specialskoletillæg') {
        list = this.specialSkoleSupplementsToApprove
      } else if (this.selectedTab === 'opgaver') {
        list = this.supplementsSorted.filter(supplement => String(supplement.code) !== '(Lønbrøk)' && !this.isUvSupplement(supplement) && !this.isSpecialSkoleSupplement(supplement) && this.supplementMatchesBeregning(supplement))
      } else {
        return []
      }
      return list.filter(supplement => (supplement.state || 'draft') === 'draft')
    },
    // Tabs i modalen der adskiller kladder fra allerede godkendte/sendte linjer.
    stateTabs () {
      const draftCount = this.categoryFiltered.filter(s => (s.state || 'draft') === 'draft').length
      const doneCount = this.categoryFiltered.filter(s => s.state === 'approved').length
      return [
        {id: 'draft', title: 'Klar til afsendelse (' + draftCount + ')'},
        {id: 'done', title: 'Godkendte (' + doneCount + ')'}
      ]
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    costCenters () {
      return this.$store.getters.costCentersAll || []
    },
    costCenterMap () {
      const map = {}
      this.costCenters.forEach(costCenter => {
        map[costCenter.id] = costCenter
      })
      return map
    },
    supplementsSorted () {
      return this.supplements
        .map(supplement => {
          const user = this.allUsersInHash[supplement.user_id]
          return {
            ...supplement,
            vUserName: user ? user.name : 'Ukendt bruger'
          }
        })
        .sort((a, b) => {
          const nameA = a.vUserName.toLowerCase()
          const nameB = b.vUserName.toLowerCase()
          if (nameA < nameB) return -1
          if (nameA > nameB) return 1
          return 0
        })
    },
    categoryFiltered () {
      if (this.selectedTab === '(Lønbrøk)') {
        return this.supplementsSorted.filter(supplement => String(supplement.code) === '(Lønbrøk)')
      }
      if (this.selectedTab === 'undervisningstillæg') {
        return this.supplementsSorted.filter(supplement => this.isUvSupplement(supplement))
      }
      if (this.selectedTab === 'specialskoletillæg') {
        return this.supplementsSorted.filter(supplement => this.isSpecialSkoleSupplement(supplement))
      }
      if (this.selectedTab === 'opgaver') {
        return this.supplementsSorted.filter(supplement => String(supplement.code) !== '(Lønbrøk)' && !this.isUvSupplement(supplement) && !this.isSpecialSkoleSupplement(supplement) && this.supplementMatchesBeregning(supplement))
      }
      return this.supplementsSorted
    },
    supplementsFiltered () {
      if (!this.isInOpusModal) {
        return this.categoryFiltered
      }
      // I modalen adskilles kladder fra godkendte/sendte, så godkendte ikke sendes til løn igen.
      return this.categoryFiltered.filter(supplement => this.matchesStateFilter(supplement))
    }
  },
  watch: {
    schoolYearId (val) {
      this.readSupplements()
    },
    // Modalens footer-knap øger denne tæller for at sende de aktuelle tillæg til OPUS.
    sendToOpusTrigger () {
      if (this.isInOpusModal) {
        this.approveSupplements()
      }
    },
    supplementsToApprove () {
      this.emitApproveState()
    },
    isApproving () {
      this.emitApproveState()
    },
    selectedTab () {
      this.emitApproveState()
    },
    stateFilter () {
      this.emitApproveState()
    },
    // Ansættelser loades asynkront; når de er klar skal "mangler ansættelse"-tilstanden
    // genberegnes, så Send til SD-knappen får den rigtige (de)aktiverede tilstand.
    employmentsInUserIdHash () {
      this.emitApproveState()
    }
  },
  methods: {
    stateLabel (state) {
      const map = {draft: 'Kladde', approved: 'Godkendt'}
      return map[state] || 'Kladde'
    },
    isUvSupplement (supplement) {
      return String(supplement.rule) === 'undervisningstillæg' || this.supplementMatchesCodes(supplement, this.uvSalaryCodes)
    },
    isSpecialSkoleSupplement (supplement) {
      return String(supplement.rule) === 'specialskoletillæg'
    },
    // Matcher et tillæg mod et kodesæt: supplementets lønart (code) tjekkes direkte, og
    // for SD slås lønkoden op via salary_code_id, så der også kan matches på tillægsnummer.
    supplementMatchesCodes (supplement, filter) {
      if (!filter) return false
      const code = String(supplement.code)
      if (filter.loenarter.indexOf(code) > -1) return true
      const salaryCode = this.salaryCodesById[supplement.salary_code_id]
      if (salaryCode) {
        if (filter.loenarter.indexOf(String(salaryCode.loenart)) > -1) return true
        if (filter.tillaegsnumre.indexOf(String(salaryCode.tillaegsnummer)) > -1) return true
      }
      return false
    },
    // Matcher et opgave-tillæg mod den valgte beregnings lønkoder, så DSA/tale-høre og
    // pædagog med eneansvar vises adskilt. Uden for modalen (ingen beregningType) matcher alt.
    supplementMatchesBeregning (supplement) {
      const filter = this.beregningFilters[this.beregningType]
      if (!filter) return true
      return this.supplementMatchesCodes(supplement, filter)
    },
    deleteSupplement (supplementId) {
      this.isDeleting = true
      docendo.deleteSalarySupplement(this.schoolId, this.schoolYearId, supplementId, success => {
        this.setIsCreateModal(false)
        this.isDeleting = false
        this.readSupplements()
      }, failure => {
        console.log('failed')
      })
    },
    setEditSupplement (supplement) {
      if (this.isInOpusModal && this.stateFilter === 'done') return
      this.editSupplement = supplement
      this.setIsCreateModal(true)
    },
    setIsShowTasksModal (bool) {
      this.isShowTasksModal = bool
      if (bool) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    formatPeriod (periodStr) {
      if (!periodStr || periodStr.indexOf('...') === -1) return ''
      const parts = periodStr.split('...')
      return moment(parts[0]).format('DD-MM-YYYY') + ' til ' + moment(parts[1]).format('DD-MM-YYYY')
    },
    formatDecimal (val) {
      const num = parseFloat(val)
      return isNaN(num) ? '—' : num.toFixed(4).replace('.', ',')
    },
    supplementTypeTitle (supplementType) {
      if (supplementType === 'lønbrøk') return 'Lønbrøk'
      if (supplementType === 'undervisningstillæg') return 'UV-tillæg'
      if (supplementType === 'specialskoletillæg') return 'Spec-skole uv-tillæg'
      if (supplementType === 'opgaver') return 'Opgaver med tillæg'
      return 'Tillæg'
    },
    generateSalarySupplements (supplementType) {
      if (this.isGeneratingSupplementType) return
      if (supplementType === 'undervisningstillæg') {
        this.$store.dispatch('trackEvent', 'supplements_uvTillaeg_generate')
      }
      if (supplementType === 'specialskoletillæg') {
        this.$store.dispatch('trackEvent', 'supplements_specSkoleUvTillaeg_generate')
      }
      this.isGeneratingSupplementType = supplementType
      docendo.generateSalarySupplements(this.schoolId, this.schoolYearId, supplementType, success => {
        this.response = Array.isArray(success) ? success : []
        this.isShowResponseModal = this.response.length > 0
        this.readSupplements()
        this.isGeneratingSupplementType = null
        eventbus.toast = {text: this.supplementTypeTitle(supplementType) + ' er hentet', success: true, position: 'bottomLeft'}
        // this.supplements = success
      }, failure => {
        console.log(failure)
        this.isGeneratingSupplementType = null
        eventbus.toast = {text: 'Kunne ikke hente ' + this.supplementTypeTitle(supplementType).toLowerCase(), success: false, position: 'bottomLeft'}
      })
    },
    approveSupplements () {
      if (this.isApproving) return
      this.doApprove()
    },
    doApprove () {
      if (this.isApproving) return
      const ids = this.supplementsToApprove.map(supplement => supplement.id)
      if (ids.length === 0) return
      this.isApproving = true
      docendo.approveSalarySupplements(this.schoolId, this.schoolYearId, ids, success => {
        this.isApproving = false
        if (success && success.status === 'error') {
          eventbus.toast = {text: success.message || ('Kunne ikke godkende og overføre til ' + this.salaryTargetLabel), success: false, position: 'bottomLeft'}
          return
        }
        this.readSupplements()
        eventbus.toast = {text: 'Sender tillæggene til ' + this.salaryTargetLabel + ' – find dem i udbakke eller afsendt', success: true, position: 'bottomLeft'}
      }, failure => {
        console.log(failure)
        this.isApproving = false
        eventbus.toast = {text: failure && failure.message ? failure.message : ('Kunne ikke godkende og overføre til ' + this.salaryTargetLabel), success: false, position: 'bottomLeft'}
      })
    },
    // Sender den aktuelle "kan sendes til OPUS"-tilstand op til modalen, så footer-knappen
    // kan vise antal/loading og være deaktiveret når der ikke er nogen lønlinjer.
    emitApproveState () {
      if (!this.isInOpusModal) return
      const drafts = this.supplementsToApprove
      // Tillæg kan ikke sendes før der er valgt en gyldig ansættelse på alle linjer (fx DSA/tale-høre
      // og pædagog med eneansvar, hvor ansættelsen vælges manuelt, samt lønbrøk hvor en evt.
      // forhåndsvalgt ansættelse ikke nødvendigvis er valgbar). En linje tæller som mangelfuld,
      // hvis dens employment_id ikke matcher en af de valgbare ansættelser i dropdownen.
      const missingEmployment = drafts.length > 0 && drafts.some(supplement => !this.hasSelectableEmployment(supplement))
      this.$emit('approveState', {
        count: drafts.length,
        isApproving: this.isApproving,
        missingEmployment: missingEmployment,
        // Footer-knappen vises kun på "Klar til afsendelse"-fanen; godkendte sendes ikke igen.
        // Gælder begge lønsystemer (OPUS for KMD, SD for Silkeborg Data).
        canApprove: this.isSalaryIntegrationActive &&
          (this.selectedTab === 'undervisningstillæg' || this.selectedTab === 'specialskoletillæg' || this.selectedTab === 'opgaver' ||
            (this.selectedTab === '(Lønbrøk)' && this.$store.getters.salarySystem === 'SD')) &&
          this.stateFilter === 'draft'
      })
    },
    matchesStateFilter (supplement) {
      const state = supplement.state || 'draft'
      if (this.stateFilter === 'draft') {
        return state === 'draft'
      }
      return state === 'approved'
    },
    employmentsForUser (userId) {
      return this.employmentsInUserIdHash[userId] || []
    },
    // Kun aktive ansættelser med en stillingskategori (employment_template) kan vælges, når
    // tillægget sendes til lønsystemet – lønkode/UV-grænse hænger på stillingskategorien.
    selectableEmploymentsForUser (userId) {
      return this.employmentsForUser(userId).filter(employment =>
        !employment.deleted &&
        employment.employment_template_id != null && employment.employment_template_id !== '')
    },
    // En linje har en gyldig ansættelse, hvis dens employment_id matcher en af de valgbare
    // ansættelser i dropdownen. En forhåndsvalgt ansættelse, der ikke er valgbar (og derfor vises
    // tom i dropdownen), tæller ikke – ellers ville Send til SD kunne klikkes med en tom dropdown.
    hasSelectableEmployment (supplement) {
      if (!supplement.employment_id) return false
      return this.selectableEmploymentsForUser(supplement.user_id)
        .some(employment => employment.id === supplement.employment_id)
    },
    employmentLabel (employment) {
      const number = employment.employment_number ? String(employment.employment_number) : ''
      const title = employment.source_title || employment.title || 'Ansættelse'
      return number ? (number + ' - ' + title) : title
    },
    // Lønkode-kolonnen viser lønarten (supplement.code), men hos SD identificeres lønkoden på
    // det 6-cifrede tillægsnummer. Slå lønkoden op via salary_code_id og vis tillægsnummeret
    // når lønsystemet er SD, med fallback til lønarten (fx for (Lønbrøk) uden salary_code_id).
    salaryCodeDisplay (supplement) {
      if (this.$store.getters.salarySystem === 'SD') {
        const salaryCode = this.salaryCodesById[supplement.salary_code_id]
        if (salaryCode && salaryCode.tillaegsnummer != null && salaryCode.tillaegsnummer !== '') {
          return salaryCode.tillaegsnummer
        }
      }
      return supplement.code
    },
    costCenterDisplay (supplement) {
      const costCenter = this.costCenterMap[supplement.cost_center_id]
      if (!costCenter) return '—'
      return this.costCenterLabel(costCenter)
    },
    costCenterLabel (costCenter) {
      const code = costCenter.code != null ? String(costCenter.code) : ''
      const name = costCenter.name || ''
      if (code && name && code !== name) return code + ' - ' + name
      return name || code || '—'
    },
    selectedEmploymentLabel (supplement) {
      const employment = this.employmentsForUser(supplement.user_id)
        .find(emp => emp.id === supplement.employment_id)
      return employment ? this.employmentLabel(employment) : 'Ingen ansættelse'
    },
    onSelectEmployment (supplement, event) {
      const value = event.target.value
      const employmentId = value ? parseInt(value, 10) : null
      this.persistEmployment(supplement, employmentId)
    },
    // Gemmer den valgte ansættelse på tillægget. updateSalarySupplement kræver de øvrige
    // (presence-validerede) felter, så supplementets eksisterende værdier sendes med uændret.
    persistEmployment (supplement, employmentId) {
      if (this.savingEmploymentIds.includes(supplement.id)) return
      this.savingEmploymentIds.push(supplement.id)
      docendo.updateSalarySupplement(
        this.schoolId,
        this.schoolYearId,
        supplement.id,
        supplement.user_id,
        employmentId,
        supplement.year_norm_hours,
        null,
        null,
        supplement.code,
        supplement.salary_code_id,
        supplement.cost_center_id,
        supplement.units,
        supplement.installments,
        supplement.units_per_installment,
        success => {
          this.savingEmploymentIds = this.savingEmploymentIds.filter(id => id !== supplement.id)
          const idx = this.supplements.findIndex(s => s.id === supplement.id)
          if (idx > -1) {
            this.$set(this.supplements, idx, Object.assign({}, this.supplements[idx], {employment_id: employmentId}))
          }
          eventbus.toast = {text: 'Ansættelse opdateret', success: true, position: 'bottomLeft'}
        },
        failure => {
          console.log(failure)
          this.savingEmploymentIds = this.savingEmploymentIds.filter(id => id !== supplement.id)
          eventbus.toast = {text: 'Kunne ikke opdatere ansættelsen', success: false, position: 'bottomLeft'}
        }
      )
    },
    setIsCreateModal (bool) {
      this.isCreateModal = bool
      if (!bool) {
        this.theEditSupplementType = null
        this.editSupplement = null
      }
    },
    readSupplements () {
      console.log('readSupplements')
      this.isLoading = true
      if (!this.schoolYearId || !this.schoolId) {
        return
      }
      docendo.listSalarySupplements(this.schoolId, this.schoolYearId, success => {
        if (success.status && success.status === 'error') {
          this.supplements = []
        } else {
          this.supplements = success
        }
        this.isLoading = false
      })
    },
    createSupplement (supplement) {
      this.isSubmitting = true

      const {
        userId,
        employmentId,
        yearNormHours,
        startDate,
        endDate,
        code,
        salaryCodeId,
        costCenterId,
        units,
        installments,
        unitsPerInstallment
      } = supplement

      docendo.createSalarySupplement(
        this.schoolId,
        this.schoolYearId,
        userId,
        employmentId,
        yearNormHours,
        startDate,
        endDate,
        code,
        salaryCodeId,
        costCenterId,
        units,
        installments,
        unitsPerInstallment,
        success => {
          console.log({success})
          this.readSupplements()
          this.isSubmitting = false
          this.setIsCreateModal(false)
        },
        failure => {
          console.log(failure)
          this.isSubmitting = false
        }
      )
    },
    updateSupplement (supplement) {
      this.isSubmitting = true

      const {
        id,
        userId,
        employmentId,
        yearNormHours,
        startDate,
        endDate,
        code,
        salaryCodeId,
        costCenterId,
        units,
        installments,
        unitsPerInstallment
      } = supplement

      docendo.updateSalarySupplement(
        this.schoolId,
        this.schoolYearId,
        id,
        userId,
        employmentId,
        yearNormHours,
        startDate,
        endDate,
        code,
        salaryCodeId,
        costCenterId,
        units,
        installments,
        unitsPerInstallment,
        success => {
          console.log({success})
          this.readSupplements()
          this.isSubmitting = false
          this.setIsCreateModal(false)
        },
        failure => {
          console.log(failure)
          this.isSubmitting = false
        }
      )
    },
    dataToExcelSimple () {
      const includeState = this.isInOpusModal && this.isSalaryIntegrationActive
      return this.supplementsFiltered.map(supplement => {
        const row = {
          'name': supplement.vUserName || '',
          'identity': this.isInOpusModal ? this.selectedEmploymentLabel(supplement) : '',
          'year_norm': supplement.year_norm_hours || '',
          'period': this.formatPeriod(supplement.valid_period) || '',
          'supplement_type': this.salaryCodeDisplay(supplement) || '',
          'account': this.costCenterDisplay(supplement),
          'units_in_period': this.formatDecimal(supplement.units) || '',
          'installments': supplement.installments || '',
          'units_per_month': this.formatDecimal(supplement.units_per_installment) || ''
        }
        if (includeState) {
          row['state'] = this.stateLabel(supplement.state)
        }
        return row
      })
    },
    asyncLoadXLSXAndDownloadSimple () {
      this.isDownloading = true
      if (window.XLSX && XLSX) {
        this.downloadSimple()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        let intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.downloadSimple()
          }
        }, 500)
      }
    },
    downloadSimple () {
      const includeState = this.isInOpusModal && this.isSalaryIntegrationActive
      const headers = {
        'name': 'Navn',
        'identity': this.isInOpusModal ? 'Ansættelse' : 'Identitet',
        'year_norm': 'Brutto årsnorm',
        'period': 'Beregningsperiode',
        'supplement_type': 'Lønkode',
        'account': 'Omkostningssted',
        'units_in_period': 'Enheder i periode',
        'installments': 'Antal udbetalinger',
        'units_per_month': 'Enh. pr måned'
      }
      if (includeState) {
        headers['state'] = 'Status'
      }

      const rows = this.dataToExcelSimple()

      rows.unshift(headers)

      const sheet = XLSX.utils.json_to_sheet(rows, {skipHeader: true})
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, sheet, 'data')
      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      const filename = `tillaeg_eksport_${timestamp}.xlsx`
      XLSX.writeFile(wb, filename)

      this.isDownloading = false
    }
  },
  created () {
    if (this.isInOpusModal) {
      // Fanen styres af den valgte beregning – brugeren kan ikke selv vælge fane i modalen.
      this.selectedTab = this.tabForBeregningType
      // Ansættelser bruges til at vælge medarbejdernummer pr. tillæg før afsendelse til OPUS.
      this.$store.dispatch('readEmployments')
      this.emitApproveState()
    }
  },
  mounted () {
    this.readSupplements()
    this.$store.dispatch('readCostCenters')
    this.$store.dispatch('readSalaryCodes')
  }
}
</script>

<style scoped>

.supplement-types {
  padding: 1rem;
  max-width: 1440px;
}

.supplement-types--full {
  max-width: none;
  padding: 0;
}

.do-table--fixed {
  table-layout: fixed;
}

.td-identitet {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
}

.employment-select {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employment-select:disabled {
  opacity: 0.6;
  cursor: default;
}

.employment-selected {
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employment-select__empty {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  font-style: italic;
  color: #6b7280;
}

.supplement-types__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.do-table tr:nth-child(2n) {
  background-color: #f9fafb;
}

.do-table tr:hover .width-50__icon {
  opacity: 1;
}

.supplement-types__header__left {
  display: flex;
  align-items: center;
  gap: 1rem
}

.width-period {
  width: 185px;
}

.supplement-list-item:hover {
  cursor: pointer;
  background: aliceblue !important;
}

.supplement-list-item--locked:hover {
  cursor: default;
  background: transparent !important;
}

.supplement-state {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.supplement-state--draft {
  background: #eef0f3;
  color: #6b7280;
}

.supplement-state--approved {
  background: #fff4d6;
  color: #a26206;
}

.supplement-state--sent {
  background: #dcf3e3;
  color: #1c7c43;
}
</style>



// WEBPACK FOOTER //
// SupplementsList.vue?ee33e19c